import type { LucideIcon } from 'lucide-react'
import { FileText, CheckCircle2, Archive, Clock, AlertCircle } from 'lucide-react'
import { DayOfWeek, DayMetrics, WeeklyMetrics } from '@/types/publication'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/utils/cn'
import { PainelEmpty, PainelTone, painelToneClass } from './PainelCard'

interface CompactMetricsBarProps {
  metrics: WeeklyMetrics
  selectedDay: DayOfWeek
  onDayChange: (day: DayOfWeek) => void
}

interface MetricBadgeProps {
  value: number
  label: string
  icon: LucideIcon
  tone: PainelTone
  tooltip?: string
}

interface DayButtonProps {
  day: DayMetrics
  isActive: boolean
  onClick: () => void
}

// Ordem dos dias da semana: Segunda a Domingo
const WEEK_DAYS: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

function MetricBadge({ value, label, icon: Icon, tone, tooltip }: MetricBadgeProps) {
  return (
    <div className="flex min-w-0 items-center gap-3" title={tooltip}>
      <div className="rounded-lg bg-muted p-2.5">
        <Icon className={cn('size-5', painelToneClass[tone])} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-semibold tabular-nums text-foreground">{value}</p>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      </div>
    </div>
  )
}

function MetricTile({ value, label, icon: Icon, tone }: Omit<MetricBadgeProps, 'tooltip'>) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-muted/40 p-3">
      <div className="mb-1 flex items-center gap-2">
        <Icon className={cn('size-4 shrink-0', painelToneClass[tone])} aria-hidden="true" />
        <span className="truncate text-xs font-medium uppercase text-foreground">{label}</span>
      </div>
      <p className="text-2xl font-semibold tabular-nums text-foreground">{value}</p>
    </div>
  )
}

function DayButton({ day, isActive, onClick }: DayButtonProps) {
  const hasData = day.received > 0

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        'relative flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors sm:size-11',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'bg-primary text-primary-foreground shadow-sm'
          : hasData
            ? 'bg-muted text-foreground hover:bg-accent'
            : 'bg-muted/40 text-muted-foreground hover:bg-muted',
        day.isToday && !isActive && 'ring-2 ring-ring ring-offset-2 ring-offset-background'
      )}
      title={`${day.dayFullLabel} - ${day.date}\n${day.received} recebidas, ${day.pending} não tratadas`}
    >
      {day.dayLabel}
      {hasData && !isActive && (
        <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-card bg-primary" />
      )}
    </button>
  )
}

/** Mini-barra tratadas (primary, à esquerda) x pendentes (destructive, à direita) do dia. */
function DayProgress({ day }: { day: DayMetrics }) {
  const processedPercentage = day.received > 0 ? (day.processed / day.received) * 100 : 0
  const pendingPercentage = day.received > 0 ? (day.pending / day.received) * 100 : 0

  return (
    <div
      className="relative h-1.5 w-11 overflow-hidden rounded-xs bg-muted"
      title={`${day.dayLabel}: ${day.processed} tratadas, ${day.pending} pendentes`}
    >
      {day.processed > 0 && (
        <div className="absolute left-0 h-full bg-primary" style={{ width: `${processedPercentage}%` }} />
      )}
      {day.pending > 0 && (
        <div className="absolute right-0 h-full bg-destructive" style={{ width: `${pendingPercentage}%` }} />
      )}
    </div>
  )
}

export function CompactMetricsBar({ metrics, selectedDay, onDayChange }: CompactMetricsBarProps) {
  const dayMetrics = metrics.byDayOfWeek[selectedDay]

  if (!dayMetrics) {
    return (
      <Card>
        <CardContent>
          <PainelEmpty icon={AlertCircle}>Nenhum dado disponível para esta semana</PainelEmpty>
        </CardContent>
      </Card>
    )
  }

  const weekDays = WEEK_DAYS.flatMap(dayKey => {
    const day = metrics.byDayOfWeek[dayKey]
    return day ? [{ dayKey, day }] : []
  })

  return (
    <Card>
      <CardContent className="flex min-w-0 flex-col gap-4">
        {/* Layout Desktop */}
        <div className="hidden items-center justify-between gap-6 xl:flex">
          <div className="flex flex-1 items-center gap-8">
            <MetricBadge
              value={dayMetrics.received}
              label="RECEBIDAS HOJE"
              icon={FileText}
              tone="primary"
              tooltip={`Total de publicações recebidas em ${dayMetrics.dayFullLabel}`}
            />
            <Separator orientation="vertical" className="h-12" />
            <MetricBadge
              value={dayMetrics.processed}
              label="TRATADAS HOJE"
              icon={CheckCircle2}
              tone="primary"
              tooltip="Publicações analisadas ou com tarefas criadas"
            />
            <Separator orientation="vertical" className="h-12" />
            <MetricBadge
              value={dayMetrics.discarded}
              label="DESCARTADAS HOJE"
              icon={Archive}
              tone="muted"
              tooltip="Publicações arquivadas como irrelevantes"
            />
            <Separator orientation="vertical" className="h-12" />
            <MetricBadge
              value={dayMetrics.pending}
              label="NÃO TRATADAS HOJE"
              icon={Clock}
              tone="destructive"
              tooltip="Publicações aguardando análise"
            />
          </div>

          {/* Seletor de Dia da Semana com Mini-Gráficos */}
          <div className="flex items-end gap-1.5 border-l border-border pl-6">
            {weekDays.map(({ dayKey, day }) => (
              <div key={dayKey} className="flex flex-col items-center gap-1">
                <DayProgress day={day} />
                <DayButton day={day} isActive={selectedDay === dayKey} onClick={() => onDayChange(dayKey)} />
              </div>
            ))}
          </div>
        </div>

        {/* Layout Mobile/Tablet */}
        <div className="flex flex-col gap-4 xl:hidden">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground">Selecione o dia</h3>
              <p className="text-xs text-muted-foreground">{dayMetrics.dayFullLabel}, {dayMetrics.date}</p>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {weekDays.map(({ dayKey, day }) => (
                <DayButton
                  key={dayKey}
                  day={day}
                  isActive={selectedDay === dayKey}
                  onClick={() => onDayChange(dayKey)}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <MetricTile value={dayMetrics.received} label="Recebidas" icon={FileText} tone="primary" />
            <MetricTile value={dayMetrics.processed} label="Tratadas" icon={CheckCircle2} tone="primary" />
            <MetricTile value={dayMetrics.discarded} label="Descartadas" icon={Archive} tone="muted" />
            <MetricTile value={dayMetrics.pending} label="Não Tratadas" icon={Clock} tone="destructive" />
          </div>
        </div>

        {/* Informações adicionais */}
        <div className="flex flex-col justify-between gap-3 border-t border-border pt-4 text-sm text-muted-foreground md:flex-row md:items-center">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-6">
            <span className="hidden md:block">
              <strong className="text-foreground">{dayMetrics.dayFullLabel}</strong> - {dayMetrics.date}
            </span>
            <span className="hidden md:block">•</span>
            <span>
              Semana: {metrics.summary.weekReceived} recebidas, {metrics.summary.weekPending} pendentes
            </span>
          </div>

          {metrics.backlog.totalPending > 0 && (
            <Badge variant="destructive" className="h-auto gap-1.5 px-3 py-1">
              <AlertCircle aria-hidden="true" />
              {metrics.backlog.totalPending} no backlog total
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
